import type { Dispatch } from 'redux'

import {
  initializePicker,
  loadBottomData,
  selectBottomSelectedItems,
  selectGroupLevel1Id,
  selectGroupPickerId,
  selectGroupTopOperator,
  selectPickerIdsForGroup,
  setBottomDisabled,
  setBottomSelectedItems,
  setGroupLevel1Config,
  setGroupLevel2Config,
  setGroupPickerId,
  setGroupSingleLevel,
  setGroupSubtitle,
  setGroupTitle,
} from 'slices/aiSegmentBuilder/picker'

import type {
  PickerSubmissionPayload,
  ServerLevelConfig,
  ServerPickerResponse,
} from './hooks'
import { endpointConfigOf, initializePickerGroup } from './hooks'

// ═══════════════════════════════════════════════════════════════════════════
// Loading Functions (single-level pickers)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Load group configuration for a single-level picker.
 * Copies level_1 config into both level_1 AND level_2 slots so that
 * BottomSelectorContainer (which reads level_2 config) uses level_1's endpoint.
 */
const loadSingleLevelGroupConfig = (
  dispatch: Dispatch,
  groupId: string,
  levelConfig: ServerLevelConfig
): void => {
  const endpointConfig = endpointConfigOf(levelConfig)

  dispatch(
    setGroupLevel1Config({
      groupId,
      id: levelConfig.id,
      label: levelConfig.label,
      operatorOptions: levelConfig.operatorOptions,
      endpointConfig,
      keyField: levelConfig.keyField,
      valueField: levelConfig.valueField,
      sources: levelConfig.sources,
      defaultSource: levelConfig.defaultSource,
    })
  )

  // Mirror into level_2 slot for BottomSelectorContainer
  dispatch(
    setGroupLevel2Config({
      groupId,
      id: levelConfig.id,
      label: levelConfig.label,
      operatorOptions: levelConfig.operatorOptions,
      endpointConfig,
      subtitle: levelConfig.subtitle ?? null,
      keyField: levelConfig.keyField,
      valueField: levelConfig.valueField,
      sources: levelConfig.sources,
      defaultSource: levelConfig.defaultSource,
    })
  )
}

/**
 * Load a complete single-level picker group from server response.
 *
 * Single-level pickers have no level_2 config. All selections are loaded into
 * a single BottomSelectorContainer (multiselect dropdown). The level_1 config
 * is copied into the level_2 slot so that BottomSelectorContainer can use it.
 *
 * @param startingPickerId - The first picker ID to use (ensures globally unique IDs)
 */
export const loadSingleLevelPickerGroup = (
  dispatch: Dispatch,
  groupId: string,
  serverResponse: ServerPickerResponse,
  startingPickerId: number = 0
): number => {
  const { picker_id, title, subtitle, level_1, selections } = serverResponse

  // Step 1: Initialize picker group with topOperator
  const topOperator = level_1.operator ?? 'any'
  initializePickerGroup(dispatch, groupId, topOperator)

  // Step 2: Set group pickerId (for submission payload)
  dispatch(setGroupPickerId({ groupId, pickerId: picker_id }))

  // Step 3: Set group title and subtitle
  dispatch(setGroupTitle({ groupId, title }))
  dispatch(setGroupSubtitle({ groupId, subtitle }))

  // Step 4: Mark as single-level
  dispatch(setGroupSingleLevel({ groupId, singleLevel: true }))

  // Step 5: Load single-level config
  // Copy level_1 config into both level_1 AND level_2 slots
  // so BottomSelectorContainer (multiselect) can use level_1's endpoint/config
  loadSingleLevelGroupConfig(dispatch, groupId, level_1)

  // Step 6: Create ONE picker with all selections as bottom items
  const pickerId = startingPickerId
  dispatch(initializePicker({ groupId, pickerId }))
  dispatch(setBottomDisabled({ groupId, pickerId, disabled: false }))

  if (selections.length > 0) {
    const allKeys = selections.map(s => s.level_1)
    const allItemsData = selections.map(s => ({
      id: s.level_1,
      name: s.level_1_label ?? s.level_1,
    }))
    dispatch(
      loadBottomData({
        groupId,
        pickerId,
        data: allItemsData,
        totalCount: allItemsData.length,
      })
    )
    dispatch(setBottomSelectedItems({ groupId, pickerId, keys: allKeys }))
  }

  return pickerId
}

// ═══════════════════════════════════════════════════════════════════════════
// Submission Payload Builder (single-level pickers)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Build the submission payload for a single-level picker group.
 * Collects all bottom (multiselect) selections into a single level.
 */
export const buildSingleLevelPayload = (
  state: any,
  groupId: string
): PickerSubmissionPayload => {
  const pickerId = selectGroupPickerId(state, groupId)
  const topOperator = selectGroupTopOperator(state, groupId)
  const level1Id = selectGroupLevel1Id(state, groupId)
  const pickerIds = selectPickerIdsForGroup(state, groupId)

  const selectionIds: string[] = []
  for (const pid of pickerIds) {
    const bottomKeys = selectBottomSelectedItems(state, pid)
    for (const key of bottomKeys) {
      selectionIds.push(key)
    }
  }

  return {
    picker_id: pickerId,
    levels: [
      {
        id: level1Id,
        operator: topOperator,
        selection_ids: selectionIds,
      },
    ],
  }
}
